import { useRef, useMemo, useEffect } from 'react';

import { useQueries, useQueryClient, useQuery } from '@gogame/query';
import { useScreenMessage } from '@gogame/screens';
import { useScreenDispatch } from '@gogame/screens';

import { CANCEL_LOADING } from 'scenes/Trackers/Tracker/reducers';
import { updateUsedMetrics } from 'scenes/Trackers/Tracker/sources/metrics';
import {
  loadTable,
  loadTableExpand,
} from 'scenes/Trackers/Tracker/sources/table';

import {
  DIMENSION_AUTHOR,
  DIMENSION_CONCEPT,
  DIMENSION_CREATIVE,
  DIMENSION_LANGUAGE,
  DIMENSION_LENGTH,
  DIMENSION_MEDIA_TYPE,
  DIMENSION_ORIENTATION,
  DIMENSION_RESOLUTION,
  DIMENSION_TAG,
  DIMENSION_TAG_CATEGORY,
  DIMENSION_CREATIVE_TYPE,
  DIMENSION_AI_TAG,
} from '../constants';
import { convertToBreakdownPath } from './convertToBreakdownPath';
import { mainGrid, expandedGrid, grids } from './queryKeyFactory';
import { useIsLongLoading } from './useIsLongLoading';

function isCreativeBasedTotal(type) {
  return [
    DIMENSION_AUTHOR,
    DIMENSION_CONCEPT,
    DIMENSION_CREATIVE,
    DIMENSION_LANGUAGE,
    DIMENSION_LENGTH,
    DIMENSION_MEDIA_TYPE,
    DIMENSION_ORIENTATION,
    DIMENSION_RESOLUTION,
    DIMENSION_TAG,
    DIMENSION_TAG_CATEGORY,
    DIMENSION_CREATIVE_TYPE,
    DIMENSION_AI_TAG,
  ].includes(type);
}

function useActivateOnce(flag) {
  const _flgRef = useRef(false);

  return useMemo(() => {
    if (!_flgRef.current && flag) {
      _flgRef.current = true;
    }
    return _flgRef.current;
  }, [flag]);
}

function useCheckNewMetrics({ order, dimension }) {
  const _currentRef = useRef([]);
  const _previousRef = useRef([]);

  _currentRef.current = order;

  return () => {
    if (_previousRef.current.toString() !== _currentRef.current.toString()) {
      const newMetrics = _currentRef.current.filter(
        (m) => !_previousRef.current.includes(m)
      );
      _previousRef.current = _currentRef.current;
      if (newMetrics.length) {
        return updateUsedMetrics({
          names: newMetrics,

          dimension: dimension,
        });
      }
    }
  };
}

window.dataLayer = window.dataLayer || [];

const LOAD_MORE_LIMIT = 100;

const sharedQueryConfig = {
  retry: false,
  staleTime: 1000 * 60 * 5,
  cacheTime: 1000 * 60 * 5,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

export const useLoadTrackerTableData = (
  params,
  { enabled: queriesEnabled, ready, breakdownPath }
) => {
  const { showWarningMessage, showMessage } = useScreenMessage();
  const fetchOnMount = useActivateOnce(queriesEnabled);
  const queryClient = useQueryClient();
  const dispatch = useScreenDispatch();
  const longLoading = useIsLongLoading();
  const dimension = [...params.drillPath].pop()?.type;
  const _rowsIdsSet = useRef(null);

  if (!_rowsIdsSet.current) {
    _rowsIdsSet.current = new Set();
  }

  const checkNewMetrics = useCheckNewMetrics({
    order: params?.columns?.order,
    dimension: dimension,
  });

  const mainQuery = useQuery(
    mainGrid({ ...params, columns: [...params.columns.order].sort() }),
    (_, { signal }) => {
      checkNewMetrics();
      signal.onabort = () => {
        window.dataLayer.push({
          event: 'requestCanceled',
          event_action: 'Grid',
          event_value: 1,
        });
      };

      return loadTable(params, { signal });
    },
    {
      ...sharedQueryConfig,

      onSuccess: ({ warnings }) => {
        warnings.length && showWarningMessage(warnings, { multiple: true });
      },
      keepPreviousData: ready,
      refetchOnMount: fetchOnMount,
      enabled: queriesEnabled && ready,
    }
  );

  const breakdowns = useMemo(() => {
    return breakdownPath.length
      ? convertToBreakdownPath(
          breakdownPath.map(({ breakdownId, openRows }) => {
            return {
              breakdownId,
              openRows,
              // openRows: getExisting(openRows, mainQuery?.data?.rows),
              // openRows: openRows.filter((rorId) =>
              //   _rowsIdsSet.current.has(rorId)
              // ),
            }; // does not load open rows until loading parent (https://dev-pl.go-game.io/v3/tracking/tracker?fullscreen=true&ready=true&dimensionSearchQuery=1111&openRows=Affiliate%3A2742~Affiliate%3A574__&breakdownId=Campaign__RootAdSet)
          }),
          {
            ...params,
            dimensionSearchQuery: undefined,
            level: params.drillPath.length - 1,
            breakdownLevel: breakdownPath.length - 1,
          }
        )
      : [];
  }, [breakdownPath, params]);

  const pageRef = useRef({});
  const loadMoreParentIdRef = useRef(null);
  const focusedRowIdRef = useRef(null);

  const loadExpandedRows = useLoadExpandedRows((params) => {
    return loadTableExpand(params);
  });

  const breakdownQueries = useQueries(
    breakdowns.map((breakdownParams, __i) => {
      const { parent } = breakdownParams;

      pageRef.current[parent] = pageRef.current[parent] || {
        limit: LOAD_MORE_LIMIT,
        offset: 0,
      };

      const queryKey = expandedGrid({
        ...breakdownParams,
        columns: [...breakdownParams.columns.order].sort(),
      });

      return {
        ...sharedQueryConfig,
        queryKey: queryKey,
        keepPreviousData: true,
        initialData: { parent },
        initialDataUpdatedAt: 0,
        onSuccess: (data) => {
          if (loadMoreParentIdRef.current === data.parent) {
            loadMoreParentIdRef.current = null;
            if (data.rows.length) {
              let newPageRowsCount = data.rows.length % LOAD_MORE_LIMIT;

              focusedRowIdRef.current =
                data.rows[
                  data.rows.length - 1 - (newPageRowsCount || LOAD_MORE_LIMIT)
                ].id;
            } else {
              focusedRowIdRef.current = null;
            }
          }
        },

        queryFn: () => {
          const params = {
            ...breakdownParams,
            tableSettings: {
              ...breakdownParams.tableSettings,
              ...pageRef.current[parent],
            },
          };
          return loadExpandedRows(params);
        },
        enabled: queriesEnabled && ready && !mainQuery.isFetching,
      };
    })
  );

  useEffect(() => {
    let dismiss;
    if (longLoading) {
      dismiss = showMessage(
        'Data is still loading. You can save time by using the Export options to get the data in the background while keeping working.',
        {
          ttl: 100000,
          callback: () => {
            dispatch({
              type: CANCEL_LOADING,
            });
            queryClient.cancelQueries({ queryKey: grids(), exact: false });
          },
          callbackText: 'cancel request',
          closable: true,
        }
      )?.dismiss;
    }
    return () => {
      if (dismiss) {
        dismiss();
      }
    };
  }, [longLoading, dispatch]);

  useEffect(
    () => () => {
      if (ready) {
        mixedQueries.forEach((q) => q.remove());
      }
    },
    [ready]
  );

  const mixedQueries = useMemo(
    () => [mainQuery, ...breakdownQueries],
    [
      peekQueryCompareParams(mainQuery),
      JSON.stringify(breakdownQueries.map(peekQueryCompareParams)),
    ]
  );

  const data = useMemo(() => {
    const { data: mainData } = mixedQueries[0];
    if (mainData) {
      const loadingRowsIds = mixedQueries
        .filter(({ isFetching, isLoading }) => isLoading || isFetching)
        .map(({ data }) => data.parent);

      const openRows = [].concat(
        ...breakdownPath.map(({ openRows }) => openRows)
      );

      const _data = {
        ...mainData,
        rows: mixedQueries.reduce((_rows, query, i) => {
          const { data, refetch } = query;
          if (data?.rows && (!i || hasExistingParentRow(_rows, data.parent))) {
            const {
              rows: levelRows,
              count,
              parent,
              drillItems,
              subHeaderRows,
            } = data;

            return [
              ..._rows,
              ...subHeaderRows.map((row) => {
                return createRow(row, {
                  isTotalValue: true,
                  pinned: i === 0, // pine only main query  subHeaderRows
                  isCreativeBasedTotal:
                    i !== 0 && isCreativeBasedTotal(row.type), // show creative base total only for breakdown
                });
              }),
              ...levelRows.map((row, rowIndex) => {
                const { hierarchy, id } = row;
                const focused = focusedRowIdRef.current === id;
                if (focused) {
                  focusedRowIdRef.current = null; //clean focus
                }

                return createRow(row, {
                  canBeExtended: hierarchy.length <= breakdownPath.length,
                  loading: loadingRowsIds.includes(id),
                  _drillItems: drillItems,
                  _focused: focused,
                  open: openRows.includes(row.id),
                  canLoadMore:
                    i && // only for breakdown
                    rowIndex === levelRows.length - 1 &&
                    levelRows.length < count,
                  nextPage: () => {
                    pageRef.current[parent].limit += LOAD_MORE_LIMIT;
                    loadMoreParentIdRef.current = parent;
                    refetch();
                  },
                });
              }),
            ];
          }

          return _rows;
        }, []),
      };
      _rowsIdsSet.current = new Set(_data.rows.map(({ id }) => id));

      return _data;
    }
    return null;
  }, [mixedQueries, JSON.stringify(breakdownPath)]);

  return {
    data,
    isLoading: mainQuery.isLoading,
    isFetching:
      !mainQuery.isLoading && mixedQueries.some(({ isFetching }) => isFetching),
  };
};

function hasExistingParentRow(existingParentRows, parentId) {
  return existingParentRows.some(({ id }) => id === parentId);
}

function peekQueryCompareParams({ data, isFetching, isLoading }) {
  return JSON.stringify({ data, isFetching, isLoading });
}

function createRow(
  serverRow,
  {
    isTotalValue = false,
    canBeExtended = false,
    loading = false,
    canLoadMore = false,
    open = false,
    pinned = false,
    isCreativeBasedTotal = false,
    nextPage,
    _drillItems,
    _focused,
  }
) {
  return {
    ...serverRow,
    _focused,
    _drillItems,
    open,
    canBeExtended,
    pinned,
    loading,
    canLoadMore,
    nextPage,
    isTotalValue,
    isCreativeBasedTotal,
  };
}

function useLoadExpandedRows(asyncCallback) {
  const _timeoutRef = useRef(null);
  const _mapRef = useRef(new Map());

  const execute = async () => {
    let error = null;
    let results = null;
    const configs = Array.from(_mapRef.current.values());
    _mapRef.current.clear();
    try {
      const params = configs.map(({ params }) => params);
      results = await asyncCallback(params);
    } catch (e) {
      error = e;
    } finally {
      configs.forEach(({ resolve, reject }, i) => {
        if (error) {
          reject(error);
        } else {
          resolve(results[i]);
        }
      });
    }
  };

  return function (params) {
    return new Promise((resolve, reject) => {
      if (_timeoutRef.current) {
        clearTimeout(_timeoutRef.current);
      }

      _mapRef.current.set(params.parent, { params, resolve, reject });

      _timeoutRef.current = setTimeout(() => {
        execute();
      }, 300);
    });
  };
}
